/**
 * EmbedGalleryModal.tsx - Gallery Embed Code Generation Modal
 * 
 * This component generates and displays embed codes for integrating
 * the fundraiser gallery into external websites with customizable options.
 * 
 * Key Features:
 * - Gallery embed code generation
 * - Customizable gallery dimensions and styling
 * - Multiple embed formats (iframe, responsive)
 * - Copy-to-clipboard functionality
 * - Preview of embedded gallery
 * - Custom styling parameters for branding
 * 
 * Embed Options:
 * - Full gallery embed with all fundraisers
 * - Filtered gallery with specific fundraisers
 * - Custom dimensions and responsive options
 * - Branding and color customization
 * 
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * 
 * Dependencies: MobX store, clipboard API, portal rendering
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { X, Copy } from 'lucide-react';
import FundraiserGallery from './FundraiserGallery';
import { Toast } from './Toast';
import { GallerySettingsModel } from '../models/GalleryModels';
import { fundraiserStore } from '../stores';
import { AuthService } from '@mindmemobile/mm-shared-global';
import ToastService from './ToastService';

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 1300px;
  height: 80vh;
  max-height: 800px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 40px 60px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 24px 0;
  white-space: normal;
  overflow: visible;
  text-align: center;
  word-wrap: break-word;
  line-height: 1.2;
`;

const ContentContainer = styled.div`
  padding: 24px 40px 40px;
  display: grid;
  grid-template-columns: 1.4fr 0.6fr;
  gap: 40px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreviewTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const PreviewContainer = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 24px;
  border: 2px solid #e2e8f0;
  min-height: 400px;
  min-width: 600px;
  width: 100%;
  overflow-y: auto;
  max-height: 500px;
  
  /* Hide header elements in preview */
  .gallery-header-left,
  .gallery-header-right {
    display: none !important;
  }
  
  /* Ensure title displays properly without cutting off */
  h1 {
    font-size: 32px !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    word-wrap: normal !important;
    max-width: none !important;
    width: auto !important;
  }
`;

const CodeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CodeTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  color: #64748b;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #3b82f6;
  }

  &:active {
    color: #3b82f6;
  }
`;

const CodeTextArea = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  color: #1e293b;
  background: #f8fafc;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CodeCaption = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 8px 0 0 0;
  text-align: center;
`;

interface EmbedGalleryModalProps {
  isVisible: boolean;
  onClose: () => void;
  gallerySettings: GallerySettingsModel
}

export const EmbedGalleryModal: React.FC<EmbedGalleryModalProps> = observer(({ isVisible, onClose, gallerySettings }) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getEmbedCode = (): string => {

    const url = gallerySettings.pageName
      ? fundraiserStore.getPublicGalleryLinkByName(AuthService.shortUniqueId, gallerySettings.pageName,
        fundraiserStore.selectedPromoterId || ""
      ) : fundraiserStore.getGalleryLink(gallerySettings.id, fundraiserStore.selectedPromoterId || "");
    return `<iframe 
  src="${url}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  scrolling="yes">
</iframe>`;
  };

  const handleCopyCode = async () => {
    const code = getEmbedCode();

    try {
      await navigator.clipboard.writeText(code);
      ToastService.showSuccess('Copied embed code for gallery');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      ToastService.showSuccess('Copied embed code for gallery');
    }
  };

  if (!isVisible) return null;

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalHeader>
          <ModalTitle>Embed Gallery</ModalTitle>
        </ModalHeader>

        <ContentContainer>
          <PreviewColumn>
            <PreviewTitle>Preview</PreviewTitle>
            <PreviewContainer>
              <div style={{ transform: 'scale(0.9)', transformOrigin: 'top left', width: '111%', height: '111%' }}>
                <div style={{ pointerEvents: 'none' }}>
                  <FundraiserGallery isEmbedPreview={true} />
                </div>
              </div>
            </PreviewContainer>
          </PreviewColumn>

          <CodeColumn>
            <CodeHeader>
              <CodeTitle>Embed Code</CodeTitle>
              <CopyButton onClick={handleCopyCode}>
                <Copy size={16} />
                Copy
              </CopyButton>
            </CodeHeader>
            <CodeTextArea
              readOnly
              value={getEmbedCode()}
              placeholder="Embed code will appear here..."
            />
            <CodeCaption>Use this embed code on your website</CodeCaption>
          </CodeColumn>
        </ContentContainer>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
});